import * as React from 'react';
import { cn } from '@/lib/utils';
import { LoadingSpinner } from '@/components/ui/loading-spinner';

/**
 * Responsive breakpoint configuration for grid columns
 */
export interface GridBreakpoints {
  sm: number; // columns for small screens (≥640px)
  md: number; // columns for medium screens (≥768px)
  lg: number; // columns for large screens (≥1024px)
  xl: number; // columns for extra large screens (≥1280px)
  '2xl'?: number; // columns for 2xl screens (≥1536px)
}

/**
 * Default breakpoint configuration
 */
const defaultBreakpoints: GridBreakpoints = {
  sm: 1,  // Mobile: single column
  md: 2,  // Tablet: 2 columns
  lg: 3,  // Desktop: 3 columns
  xl: 4,  // Large desktop: 4 columns
  '2xl': 5 // Very large desktop: 5 columns
};

/**
 * Props for the VirtualizedGrid component
 */
export interface VirtualizedGridProps<T> {
  /** Array of items to display */
  items: T[];
  /** Function to render each item */
  renderItem: (item: T, index: number) => React.ReactNode;
  /** Function to extract a unique key for each item */
  keyExtractor: (item: T, index: number) => string;
  /** Number of columns at each breakpoint */
  columns?: GridBreakpoints;
  /** Gap between grid items */
  gap?: string;
  /** Whether data is loading */
  loading?: boolean;
  /** Whether there are more pages to load */
  hasNextPage?: boolean;
  /** Callback to load more items */
  onLoadNextPage?: () => void;
  /** Number of items from end to trigger load more */
  loadMoreThreshold?: number;
  /** Current view mode */
  viewMode?: 'grid' | 'list';
  /** Callback when view mode changes */
  onViewModeChange?: (mode: 'grid' | 'list') => void;
  /** Message to show when no items */
  emptyMessage?: string;
  /** Message to show while loading */
  loadingMessage?: string;
  /** Additional CSS class for the container */
  className?: string;
  /** Estimated height of each item in pixels (for virtualization) */
  itemHeight?: number;
  /** Number of items to render outside visible area (buffer) */
  overscan?: number;
}

/**
 * VirtualizedGrid Component
 *
 * A performant, responsive grid component with virtual scrolling,
 * infinite scroll capability, and support for both grid and list views.
 *
 * @example
 * ```tsx
 * <VirtualizedGrid
 *   items={streams}
 *   renderItem={(stream) => <StreamCard stream={stream} />}
 *   keyExtractor={(stream) => stream.id}
 *   columns={{ sm: 1, md: 2, lg: 3, xl: 4 }}
 *   gap="gap-4"
 *   loading={loading}
 *   hasNextPage={hasNextPage}
 *   onLoadNextPage={loadMore}
 *   loadMoreThreshold={5}
 *   viewMode={viewMode}
 *   onViewModeChange={setViewMode}
 *   emptyMessage="No streams found"
 * />
 * ```
 */
export function VirtualizedGrid<T>({
  items,
  renderItem,
  keyExtractor,
  columns = defaultBreakpoints,
  gap = 'gap-4',
  loading = false,
  hasNextPage = false,
  onLoadNextPage,
  loadMoreThreshold = 5,
  viewMode = 'grid',
  onViewModeChange,
  emptyMessage = 'No items to display',
  loadingMessage = 'Loading...',
  className,
  itemHeight = 200,
  overscan = 3,
}: VirtualizedGridProps<T>) {
  // Refs
  const containerRef = React.useRef<HTMLDivElement>(null);
  const sentinelRef = React.useRef<HTMLDivElement>(null);
  const observerRef = React.useRef<IntersectionObserver | null>(null);

  // State for visible range
  const [visibleRange, setVisibleRange] = React.useState({
    start: 0,
    end: 50 // Initial render of 50 items
  });

  // State for container dimensions
  const [containerHeight, setContainerHeight] = React.useState(600);
  const [measuredItemHeight, setMeasuredItemHeight] = React.useState(itemHeight);
  const gapSize = React.useMemo(() => {
    const numericGap = Number(gap.match(/gap-(\d+(?:\.\d+)?)/)?.[1] || 0);
    return numericGap > 0 ? numericGap * 4 : 16;
  }, [gap]);

  /**
   * Calculate the number of columns based on container width
   */
  const getColumnCount = React.useCallback((width: number): number => {
    if (width >= 1536) return columns['2xl'] ?? columns.xl;
    if (width >= 1280) return columns.xl;
    if (width >= 1024) return columns.lg;
    if (width >= 768) return columns.md;
    if (width >= 640) return columns.sm;
    return 1;
  }, [columns]);

  // Track current column count
  const [columnCount, setColumnCount] = React.useState(() => {
    if (typeof window === 'undefined' || !containerRef.current) {
      return 1;
    }
    return getColumnCount(containerRef.current.offsetWidth);
  });

  /**
   * Handle container resize to adjust column count
   */
  const handleResize = React.useCallback(() => {
    if (containerRef.current) {
      const width = containerRef.current.offsetWidth;
      setColumnCount(getColumnCount(width));
    }
  }, [getColumnCount]);

  /**
   * Calculate visible items based on scroll position
   */
  const calculateVisibleRange = React.useCallback(() => {
    if (!containerRef.current) return;

    const scrollTop = containerRef.current.scrollTop;
    const containerWidth = containerRef.current.offsetWidth;
    const currentColumnCount = viewMode === 'list' ? 1 : getColumnCount(containerWidth);

    // Calculate rows based on item height and gap
    const totalItemHeight = measuredItemHeight + gapSize;

    const startRow = Math.max(0, Math.floor(scrollTop / totalItemHeight) - overscan);
    const visibleRows = Math.ceil(containerHeight / totalItemHeight) + overscan * 2;
    const endRow = startRow + visibleRows;

    const start = startRow * currentColumnCount;
    const end = Math.min(items.length, endRow * currentColumnCount);

    setVisibleRange({ start, end });
  }, [measuredItemHeight, gapSize, overscan, containerHeight, items.length, getColumnCount, viewMode]);

  /**
   * Initialize and manage Intersection Observer for infinite scroll
   */
  React.useEffect(() => {
    if (!onLoadNextPage || !hasNextPage || loading) return;

    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    // Clean up previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && hasNextPage && !loading) {
          const remainingItems = items.length - visibleRange.end;
          if (remainingItems <= loadMoreThreshold) {
            onLoadNextPage();
          }
        }
      },
      {
        root: containerRef.current,
        rootMargin: '200px',
        threshold: 0.1
      }
    );

    observerRef.current.observe(sentinel);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [onLoadNextPage, hasNextPage, loading, items.length, visibleRange.end, loadMoreThreshold]);

  /**
   * Set up scroll listener and resize observer
   */
  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      calculateVisibleRange();
    };

    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setContainerHeight(entry.contentRect.height);
        handleResize();
      }
    });

    container.addEventListener('scroll', handleScroll, { passive: true });
    resizeObserver.observe(container);

    // Initial calculation
    calculateVisibleRange();
    handleResize();

    return () => {
      container.removeEventListener('scroll', handleScroll);
      resizeObserver.disconnect();
    };
  }, [calculateVisibleRange, handleResize]);

  /**
   * Memoize visible items to prevent unnecessary re-renders
   */
  const visibleItems = React.useMemo(() => {
    return items.slice(visibleRange.start, visibleRange.end).map((item, index) => ({
      item,
      index: visibleRange.start + index
    }));
  }, [items, visibleRange.start, visibleRange.end]);

  // Keep the estimate in sync when caller changes itemHeight (e.g., view mode switch).
  React.useEffect(() => {
    setMeasuredItemHeight(itemHeight);
  }, [itemHeight]);

  // Measure the first rendered item to stabilize virtual spacing.
  React.useEffect(() => {
    if (!containerRef.current || visibleItems.length === 0) return;

    const firstItem = containerRef.current.querySelector<HTMLElement>('[data-index]');
    if (!firstItem) return;

    const measure = () => {
      const nextHeight = Math.round(firstItem.getBoundingClientRect().height);
      if (nextHeight <= 0) return;
      if (Math.abs(nextHeight - measuredItemHeight) > 2) {
        setMeasuredItemHeight(nextHeight);
      }
    };

    measure();

    const resizeObserver = new ResizeObserver(() => {
      measure();
    });

    resizeObserver.observe(firstItem);
    return () => resizeObserver.disconnect();
  }, [visibleItems, measuredItemHeight]);

  /**
   * Render loading skeleton items
   */
  const renderSkeletons = () => {
    const skeletonCount = Math.min(12, columnCount * 2);
    return Array.from({ length: skeletonCount }).map((_, i) => (
      <div
        key={`skeleton-${i}`}
        className={cn(
          viewMode === 'grid'
            ? 'rounded-lg border bg-card p-4'
            : 'flex items-center gap-4 rounded-lg border bg-card p-4'
        )}
        aria-hidden="true"
      >
        <div className="bg-muted animate-pulse rounded" style={{ height: itemHeight - 32, width: '100%' }} />
      </div>
    ));
  };

  // Calculate total rows for sentinel positioning
  const effectiveColumnCount = viewMode === 'list' ? 1 : Math.max(1, columnCount);
  const totalRows = Math.ceil(items.length / effectiveColumnCount);
  const sentinelRow = totalRows;

  return (
    <div className="relative" style={{ height: '100%' }}>
      {/* View mode toggle — rendered outside the scroll container so it doesn't float over content */}
      {onViewModeChange && (
        <div className="absolute right-4 top-4 z-10">
          <button
            onClick={() => onViewModeChange(viewMode === 'grid' ? 'list' : 'grid')}
            className="rounded-md border bg-background p-2 shadow-sm hover:bg-accent"
            aria-label={`Switch to ${viewMode === 'grid' ? 'list' : 'grid'} view`}
          >
            {viewMode === 'grid' ? (
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="8" y1="6" x2="21" y2="6"></line>
                <line x1="8" y1="12" x2="21" y2="12"></line>
                <line x1="8" y1="18" x2="21" y2="18"></line>
                <line x1="3" y1="6" x2="3.01" y2="6"></line>
                <line x1="3" y1="12" x2="3.01" y2="12"></line>
                <line x1="3" y1="18" x2="3.01" y2="18"></line>
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7"></rect>
                <rect x="14" y="3" width="7" height="7"></rect>
                <rect x="14" y="14" width="7" height="7"></rect>
                <rect x="3" y="14" width="7" height="7"></rect>
              </svg>
            )}
          </button>
        </div>
      )}

      <div
        ref={containerRef}
        className={cn(
          'relative overflow-y-auto',
          className
        )}
        style={{ height: '100%' }}
        role="grid"
        aria-label="Virtualized grid"
      >
        {/* Grid container */}
        <div
          className={cn(
            'grid',
            gap,
            viewMode === 'list' && 'grid-rows-[auto]'
          )}
          style={{
            gridTemplateColumns: `repeat(${effectiveColumnCount}, minmax(0, 1fr))`,
            // Preserve space for items scrolled above the visible window so the
            // scroll position doesn't jump when those DOM nodes are removed.
            paddingTop: `${Math.floor(visibleRange.start / effectiveColumnCount) * (measuredItemHeight + gapSize)}px`,
            // Preserve space for items below the visible window.
            paddingBottom: `${Math.max(0, (totalRows - Math.ceil(visibleRange.end / effectiveColumnCount)) * (measuredItemHeight + gapSize))}px`
          }}
        >
          {/* Render visible items */}
          {visibleItems.map(({ item, index }) => (
            <div
              key={keyExtractor(item, index)}
              data-index={index}
              className={cn(
                viewMode === 'list' && 'contents'
              )}
            >
              {renderItem(item, index)}
            </div>
          ))}

          {/* Loading skeletons — only on initial empty load.
              When items exist, skeletons inflate content height; when they
              disappear the browser clamps scrollTop to 0 (jump-to-top bug).
              For subsequent fetches the bottom overlay spinner is shown instead. */}
          {loading && items.length === 0 && renderSkeletons()}

          {/* Sentinel for infinite scroll */}
          {hasNextPage && !loading && (
            <div
              ref={sentinelRef}
              className={cn(
                'col-span-full',
                viewMode === 'list' ? 'grid' : 'contents'
              )}
              style={{
                gridRow: sentinelRow + 1,
                height: '1px'
              }}
            />
          )}
        </div>

        {/* Empty state */}
        {!loading && items.length === 0 && (
          <div className="flex h-full items-center justify-center p-8">
            <div className="text-center text-muted-foreground">
              <p>{emptyMessage}</p>
            </div>
          </div>
        )}

        {/* Loading overlay */}
        {loading && items.length > 0 && (
          <div className="absolute inset-x-0 bottom-0 flex justify-center bg-gradient-to-t from-background to-transparent p-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <LoadingSpinner size="sm" />
              <span>{loadingMessage}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default VirtualizedGrid;
